import type { ReactNode } from "react";
import { createContext, useContext, useEffect, useState } from "react";
import { SignOutDocument } from "@/__generated__/graphql";
import { DEFAULT_USER_DATA } from "@/constants";
import { useClientUnreadNotificationCountQuery } from "@/gqlhooks/notification/useClientUnreadNotificationCountQuery";
import { ClientInstance } from "@/lib/graphql/apollo";
import { resetAnalyticUser } from "@/lib/monitoring/analytics";
import { resetUserContext } from "@/lib/monitoring/sentry";
import { storage } from "@/lib/storage";
import { useAuthState } from "@/store/auth";
import { useClientStore } from "@/store/auth/client";
import { usePackageStore } from "@/store/packages/packageStore";
import { useVisitStore } from "@/store/visits/visitStore";

import { CoreAuthProvider, useAuth as useCoreAuth } from "@luce/core/auth";

import { LoadingDialog } from "../loading-dialog";

const CLIENT_ID_KEY = "clientId";

export type LoginArgs = {
  jwt: string;
  refreshToken: string;
  clientId: string;
};

type AuthSession = {
  login: (args: LoginArgs) => void;
  logout: () => Promise<void>;
  isLoading: boolean;
  isAuthenticated: boolean;
  isGuest: boolean;
  clientId: string;
};

const AuthContext = createContext<AuthSession>({
  login: () => {},
  logout: async () => {},
  isLoading: true,
  isAuthenticated: false,
  isGuest: true,
  clientId: "",
});

export function useAuth(): AuthSession {
  return useContext<AuthSession>(AuthContext);
}

/** App teardown, run by core BEFORE it clears the token (mutation still authed). */
async function onSignOut(): Promise<void> {
  try {
    await ClientInstance.mutate({ mutation: SignOutDocument });
  } catch (error) {
    console.log("session: logout error", error);
  }
  await ClientInstance.clearStore();
  useClientStore.getState().clear();
  useVisitStore.getState().clear();
  usePackageStore.getState().clear();
  const { setUserInfo, setUnreadNotificationCount } = useAuthState.getState();
  setUserInfo(DEFAULT_USER_DATA);
  setUnreadNotificationCount(0);
  await storage.deleteAll();
  resetUserContext();
  resetAnalyticUser();
}

function AuthAdapterInner({ children }: { children: ReactNode }) {
  const { status, accountId: clientId, signIn, signOut } = useCoreAuth();
  const [signingOut, setSigningOut] = useState(false);

  const isLoading = status === "loading";
  const isGuest = status !== "signed-in";

  // Preserve the notification-count subscription the original provider ran.
  useClientUnreadNotificationCountQuery(isGuest);

  const login = (args: LoginArgs): void =>
    signIn({ jwt: args.jwt, refreshToken: args.refreshToken, accountId: args.clientId });

  const logout = async (): Promise<void> => {
    setSigningOut(true);
    try {
      await signOut();
    } finally {
      setSigningOut(false);
    }
  };

  // Hydrate the client store whenever we become signed in (login or restore).
  useEffect(() => {
    if (status === "signed-in" && clientId) {
      useClientStore.getState().fetch({ requestPayload: { id: clientId } });
    }
  }, [status, clientId]);

  return (
    <AuthContext.Provider
      value={{ isLoading, isGuest, isAuthenticated: !isGuest, login, logout, clientId }}
    >
      <LoadingDialog open={signingOut} />
      {children}
    </AuthContext.Provider>
  );
}

type Props = { children: ReactNode };

export const AuthProvider = ({ children }: Props) => {
  return (
    <CoreAuthProvider
      config={{
        storage,
        accountKey: CLIENT_ID_KEY,
        onSignOut,
      }}
    >
      <AuthAdapterInner>{children}</AuthAdapterInner>
    </CoreAuthProvider>
  );
};
